import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  ReactElement,
} from 'react'
import { DSVRowArray, scaleLinear, csv, NumberValue } from 'd3'
import styled, { useTheme } from 'styled-components'
import { animated, useSpring, useTransition } from '@react-spring/web'
import {
  ChartAxisText,
  ChartAxisTextColored,
  ChartNote,
} from 'components/styles'
import { Trans, useTranslation } from 'react-i18next'

interface Note {
  text: ReactElement
  x: number
  y: number
  orientation:
    | 'top left'
    | 'top right'
    | 'bottom left'
    | 'bottom right'
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
}

export const ChartGrowthArrows = ({
  showAnnotations,
  showLastQuintile,
  handleNewHeight,
}: {
  showAnnotations: number[]
  showLastQuintile: boolean
  handleNewHeight: (height: number) => void
}) => {
  const theme = useTheme()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [width, setWidth] = useState<number>(0)
  const [rawData, setRawData] = useState<DSVRowArray | null>(null)
  const [maxPercentile, setMaxPercentile] = useState<number>(19)
  const t = useTranslation().t

  const transitions = useTransition(showAnnotations, {
    from: { opacity: 0, translateY: 10 },
    enter: { opacity: 1, translateY: 0 },
    leave: { opacity: 0, translateY: 10 },
  })

  const svg = useRef<SVGSVGElement | null>(null)
  const svgContainer = useRef<HTMLDivElement | null>(null)

  const ratios = {
    desktop: 4 / 6,
    mobile: 2 / 1,
  }

  const margin = {
    top: 60,
    right: 40,
    bottom: 60,
    left: 60,
  }

  const height =
    width * (isMobile ? ratios.mobile : ratios.desktop) -
    margin.top -
    margin.bottom

  const data = rawData
    ? (rawData.filter((item: any) => +item.percentile < maxPercentile) as any)
    : []

  // Vertical scale (income percentiles)
  const scaleY = scaleLinear().domain([0, 20]).range([height, 0])

  // Horizontal scale (CO2)
  const scaleX = scaleLinear()
    .domain([
      0,
      Math.max.apply(
        null,
        data.map((item: any) => item['2019'])
      ) + 5,
    ])
    .range([0, width])

  const ticks = scaleX.ticks(isMobile ? 5 : 8)

  const notes: Note[] = [
    {
      x: scaleX(isMobile ? 15 : 7),
      y: scaleY(isMobile ? 15 : 15),
      orientation: 'bottom left',
      text: (
            <Trans
              i18nKey={'chChart:notes.step1.all'}
              components={{ tspan: <tspan style={{ fontWeight: 'bold' }} /> }}
            />
      ),
    },
    {
      x: scaleX(isMobile ? 6 : 13),
      y: scaleY(isMobile ? 11.85 : 12),
      orientation: 'top right',
      text: (
            <Trans
              i18nKey={'chChart:notes.step2.all'}
              components={{ tspan: <tspan style={{ fontWeight: 'bold' }} /> }}
            />
      ),
    },
    {
      x: scaleX(isMobile ? 20 : 45),
      y: scaleY(isMobile ? 15.7 : 16),
      orientation: 'top right',
      text: (
            <Trans
              i18nKey={'chChart:notes.step3.all'}
              components={{ tspan: <tspan style={{ fontWeight: 'bold' }} /> }}
            />
      ),
    },
  ]

  const handleResize = () => {
    setIsMobile(window.innerWidth < 768)

    if (svgContainer.current) {
      setWidth(
        svgContainer.current?.getBoundingClientRect().width -
          margin.left -
          margin.right
      )
    }
  }

  useLayoutEffect(() => {
    handleResize()
  }, [svgContainer])

  useEffect(() => {
    setMaxPercentile(showLastQuintile ? 20 : 19)
  }, [showLastQuintile])

  useEffect(() => {
    handleNewHeight(height + margin.top + margin.bottom)
  }, [height])

  useEffect(() => {
    csv(
      `${
        process.env.NODE_ENV === 'production'
          ? process.env.REACT_APP_DEPLOY_PATH
          : ''
      }/data/ch_emissions_growth_grouped.csv`
    ).then((d: DSVRowArray) => {
      console.log('data loaded', d)
      setRawData(d)
    })

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const helpLabelX1: NumberValue =
    data.length > 0 ? +data[data.length - 1]['1990'] : 0
  const helpLabelX2: NumberValue =
    data.length > 0 ? +data[data.length - 1]['2019'] : 0

  const hiddenYLabels = isMobile
    ? [0, 1, 2].some((el) => showAnnotations.includes(el))
      ? [0, 1]
      : [0, 1, 2, 3, 4, 5, 6]
    : []
  console.log('hiddenYLabels', hiddenYLabels)

  return (
    <SVGContainer
      ref={svgContainer}
      onClick={() => {
        setMaxPercentile(maxPercentile + 1)
      }}
    >
      {/* <div
        style={{
          position: 'absolute',
          background: 'red',
          transform: `translate(${scaleX(20) + margin.left}px, ${scaleY(5) + margin.top}px)`,
        }}
      >O</div> */}
      <svg
        ref={svg}
        width={width + margin.left + margin.right}
        height={height + margin.top + margin.bottom}
        style={{ display: 'block' }}
      >
        <g
          style={{ transform: `translate(${margin.left}px, ${margin.top}px)` }}
        >
          {/* Grid Y */}
          {data.map((item: any, i: number) => (
            <g
              key={`axis-x-line-${i}`}
              style={{ transform: `translateY(${scaleY(item.percentile)}px)` }}
            >
              <line
                x1={-10}
                y1={0}
                x2={width}
                y2={0}
                stroke={theme.chartGridColorY}
                strokeDasharray='1px 2px'
              />
              {i % 2 === 0 && !hiddenYLabels.includes(i) && (
                <ChartAxisText
                  key={`text-growth-${item.percentile}`}
                  x={-15}
                  y={0}
                  alignmentBaseline='middle'
                  textAnchor='end'
                >
                  {(+item.percentile + 1) * 5}
                  {t('chChart:axisLabel.percentileAbbr')}
                </ChartAxisText>
              )}
              {i === 19 - 1 && (
                <ChartAxisText
                  style={{ fontSize: '0.5em' }}
                  key={`text-growth-${item.percentile}`}
                  x={-15}
                  y={15}
                  alignmentBaseline='middle'
                  textAnchor='end'
                >
                  {t('chChart:axisLabel.percentile')}
                </ChartAxisText>
              )}
            </g>
          ))}

          {/* Grid X */}
          {ticks.map((tick: number, i: number) => {
            const tickX = scaleX(tick)

            return (
              <g
                key={`grid-tickX-${i}`}
                style={{ transform: `translateX(${tickX}px)` }}
              >
                <line
                  x1={0}
                  y1={25}
                  x2={0}
                  y2={height + 10}
                  stroke={theme.chartGridColorX}
                />
                {i > 0 && (
                  <ChartAxisText textAnchor='middle' y={height + 30}>
                    {tick}{' '}
                    {i === ticks.length - 1 ? (
                      <>
                        tCO
                        <tspan baseline-shift='sub' font-size='smaller'>
                          2
                        </tspan>
                      </>
                    ) : (
                      ''
                    )}
                  </ChartAxisText>
                )}
              </g>
            )
          })}

          {/* Objectif 2030 */}
          {!isMobile && (
            <ChartAxisTextColored x={scaleX(6) + 10} y={20} textAnchor='start'>
              {t('chChart:objective')}
            </ChartAxisTextColored>
          )}
          <line
            x1={scaleX(6)}
            y1={25}
            x2={scaleX(6)}
            y2={height}
            // strokeDasharray={'1px 2px'}
            strokeWidth={0.75}
            stroke={theme.brandColor}
          />
          <rect
            x={0}
            y={25}
            width={scaleX(6)}
            height={height - 25}
            fill={theme.brandColor}
            opacity={theme.colorMode == 'dark' ? 0.15 : 0.05}
          />

          {/* Dates explanation */}
          <line
            x1={scaleX(helpLabelX1)}
            y1={scaleY(data.length - 1)}
            x2={scaleX(helpLabelX1)}
            y2={scaleY(data.length - 1) - 22}
            stroke='#a5a6a7'
          />

          <ChartAxisText
            x={scaleX(helpLabelX1)}
            y={scaleY(data.length - 1) - 22}
            textAnchor='end'
          >
            1990
          </ChartAxisText>

          <line
            x1={scaleX(helpLabelX2)}
            y1={scaleY(data.length - 1)}
            x2={scaleX(helpLabelX2)}
            y2={scaleY(data.length - 1) - 22}
            stroke='#a5a6a7'
          />

          <ChartAxisText
            x={scaleX(helpLabelX2)}
            y={scaleY(data.length - 1) - 22}
            textAnchor='start'
          >
            2019
          </ChartAxisText>

          {/* Arrows */}
          {data.map((item: any) => {
            return (
              <Arrow
                key={`arrow-${item.percentile}`}
                x1={scaleX(item['1990'])}
                x2={scaleX(item['2019'])}
                y={scaleY(item.percentile) - 1}
                value={Math.round((item['2019'] - item['1990']) / item['1990'] * 1000) / 10}
              />
            )
          })}
        </g>

        <ChartAxisLabel x={15} y={70} textAnchor='start'>
          ↑ {t('chChart:axisLabel.richest')}
        </ChartAxisLabel>

        <ChartAxisLabel
          x={15}
          y={height + margin.top + margin.bottom - 15}
          textAnchor='start'
        >
          ↓ {t('chChart:axisLabel.poorest')}
        </ChartAxisLabel>
      </svg>

      {/* Annotations */}
      {transitions((style, item) => {
        const note: any = notes[item - 1]

        return (
          note && (
            <animated.div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '200px',
                background: 'var(--text-color)',
                color: 'var(--site-background)',
                padding: '1rem',
                lineHeight: '1.2',
                translateX: note.x,
                translateY: style.translateY.to((v) => v + note.y),
                opacity: style.opacity,
              }}
            >
              <ChartNoteContainer
                orientation={note.orientation}
                text={note.text}
              />
            </animated.div>
          )
        )
      })}
    </SVGContainer>
  )
}

const SVGContainer = styled.div`
  position: relative;
`

const ChartAxisLabel = styled.text`
  font-size: 0.7em;
  fill: var(--text-color);
  font-weight: 900;
  -webkit-font-smoothing: antialiased;
`

const Arrow = ({
  x1,
  x2,
  y,
  value,
}: {
  x1: number
  x2: number
  y: number
  value: number
}) => {
  const theme = useTheme()
  const strokeWidth = 2
  const dir = x2 - x1 < 0 ? 1 : -1
  const spring = useSpring({
    head: dir === 1 ? x2 : x1,
    tail: dir === 1 ? x1 : x2,
    y: y + strokeWidth / 2,
  })

  return (
    <g>
      <animated.line
        x1={spring.head}
        y1={spring.y}
        x2={spring.tail}
        y2={spring.y}
        stroke={dir === 1 ? theme.brandColor : 'var(--text-color)'}
        strokeWidth={strokeWidth}
        strokeLinecap='round'
      />
      <animated.g
        style={{
          translateX: dir === 1 ? spring.head : spring.tail,
          translateY: spring.y,
        }}
      >
        <text
          x={dir === 1 ? -10 : 10}
          y={0}
          fontSize='0.75em'
          textAnchor={dir === 1 ? 'end' : 'start'}
          alignmentBaseline='middle'
          paintOrder='stroke'
          stroke='var(--site-background)'
          strokeWidth={9}
          strokeLinecap='butt'
          strokeLinejoin='round'
          fill={dir === 1 ? theme.brandColor : 'var(--text-color)'}
        >
          {value > 0 ? `+${value}%` : `${value}%`}
        </text>

        <polyline
          points={`${4 * dir},${4 * dir} ${0},${0} ${4 * dir},${-4 * dir}`}
          stroke={dir === 1 ? theme.brandColor : 'var(--text-color)'}
          strokeWidth={strokeWidth}
          strokeLinecap='round'
          fill='transparent'
        />
      </animated.g>
    </g>
  )
}

const ChartNoteContainer = ({
  orientation,
  text,
}: {
  orientation: string
  text: string
}) => {
  const [refText, setRefText] = useState<SVGTextElement | null>(null)
  // const textBBox = refText?.getBBox()

  return (
    <NoteContainer>
      {/* {textBBox && (
        <rect
          y={-20}
          x={-10}
          width={textBBox?.width + 20}
          height={textBBox?.height + 20}
          fill='var(--text-color)'
          // filter='drop-shadow(1px 1px 5px rgb(0 0 0 / 0.1))'
        />
      )} */}
      {text}
      {/* {textBBox && ( */}
      <svg
        viewBox='0 0 20 30'
        style={{
          position: 'absolute',
          width: '50px',
          height: '50px',
          top: orientation.includes('top') ? 0 : 'auto',
          bottom: orientation.includes('bottom') ? 0 : 'auto',
          left: orientation.includes('left') ? 0 : 'auto',
          right: orientation.includes('right') ? 0 : 'auto',
          transform: `translate(${
            orientation.includes('right') ? `100%` : `-100%`
          }, ${
            orientation.includes('top') && orientation.includes('right') ? 0 : 0
          }px) scaleX(${orientation.includes('left') ? 1 : -1}) scaleY(${
            orientation.includes('top') ? -1 : 1
          })`,
        }}
      >
        <g>
          <path
            style={{ fill: 'none', stroke: 'var(--text-color)' }}
            d='M23.3,8.6C5.8,8.4,4,23,2.8,26.8'
          />
          <polyline
            style={{ fill: 'none', stroke: 'var(--text-color)' }}
            points='8,24.6 2.6,26.8 0.5,21.5 '
          />
        </g>
      </svg>
      {/* )} */}
    </NoteContainer>
  )
}

const NoteContainer = styled.div`
  font-size: 0.8em;
  line-height: 1.3;
  -webkit-font-smoothing: antialiased;
`